import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { CountBadge } from '@/components/admin/CountBadge';
import { Notice } from '@/components/admin/Notice';
import {
  absenceDecisionReason,
  createAbsenceRequest,
  decideAbsenceRequest,
  fetchAbsenceRequests,
  type AbsenceRequest,
} from '@/features/events/attendanceApi';
import { ApiError } from '@/lib/api/http';

const STATUS_LABELS: Record<AbsenceRequest['status'], string> = {
  pending: '受付中',
  approved: '承認済み',
  rejected: '差戻し',
};

/** 欠席申請セクション（指示書 §4: 理由・確認先・承認/差戻し・判断履歴）. */
export function EventAbsenceRequestsSection({
  eventId,
  canRequest = true,
}: {
  eventId: string;
  /** 受講生がこの回の対象者か（対象外には申請フォームを出さない / D-152） */
  canRequest?: boolean;
}): ReactElement {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['absence-requests', eventId],
    queryFn: () => fetchAbsenceRequests(eventId),
  });

  const isStudent = user?.active_role === 'student' && canRequest;
  const isOffice = user?.active_role === 'office';

  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ request: AbsenceRequest; status: 'approved' | 'rejected' } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const ownPending = query.data?.find((r) => r.status === 'pending');
  const pendingCount = (query.data ?? []).filter((r) => r.status === 'pending').length;

  async function onSubmitRequest(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createAbsenceRequest(eventId, reason);
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['absence-requests', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setNotice('欠席申請を送信しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '送信に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onDecide(decisionReason: string): Promise<void> {
    if (!dialog) return;
    setBusy(true);
    setError(null);
    try {
      await decideAbsenceRequest(dialog.request.id, dialog.status, decisionReason);
      await queryClient.invalidateQueries({ queryKey: ['absence-requests', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['attendance', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setDialog(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '処理に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h3 className="mb-2 inline-flex items-center gap-1.5 font-semibold text-wptext-strong">
        欠席申請
        {isOffice && <CountBadge count={pendingCount} label="未処理" />}
      </h3>
      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {isStudent && !ownPending && (
        <form
          className="mb-4 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmitRequest();
          }}
        >
          <label className="block flex-1">
            <span className="mb-1.5 block text-wptext-muted">理由</span>
            <input
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            欠席申請を送る
          </button>
        </form>
      )}

      {query.isPending && <p className="text-wptext-muted">読み込み中…</p>}
      {query.isError && <p className="text-wpnotice-error">読み込みに失敗しました。</p>}
      {query.data && query.data.length === 0 && (
        <p className="text-wptext-muted">欠席申請はありません。</p>
      )}
      {query.data && query.data.length > 0 && (
        <ul className="divide-y divide-wpborder">
          {query.data.map((request) => {
            const decided = absenceDecisionReason(request);
            return (
              <li key={request.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
                <div>
                  <p className="inline-flex flex-wrap items-center gap-1.5">
                    <span>
                      {request.member_name ?? request.member_id} — {request.reason}（
                      {STATUS_LABELS[request.status]}）
                    </span>
                    {request.status === 'pending' && (
                      <CountBadge count={1} label="未処理" />
                    )}
                  </p>
                  {decided && (
                    <p className="mt-1 text-wptext-muted">
                      {request.status === 'rejected' ? '差戻し理由' : '承認理由'}: {decided}
                    </p>
                  )}
                </div>
                {isOffice && request.status === 'pending' && (
                  <span className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setDialog({ request, status: 'approved' })}
                      className="text-wpaccent hover:underline"
                    >
                      承認
                    </button>
                    <button
                      type="button"
                      onClick={() => setDialog({ request, status: 'rejected' })}
                      className="text-wpnotice-error hover:underline"
                    >
                      差戻し
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={dialog !== null}
        title={dialog?.status === 'approved' ? '欠席申請を承認しますか？' : '欠席申請を差し戻しますか？'}
        requireReason
        confirmLabel={dialog?.status === 'approved' ? '承認する' : '差し戻す'}
        onConfirm={(decisionReason) => void onDecide(decisionReason)}
        onCancel={() => setDialog(null)}
      />
    </div>
  );
}
